import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import i18n from 'src/i18n';
import { loadStripe } from 'src/models/cart/utils';
import { fetchSetupIntent } from 'src/models/payment/payment';
import { getPaymentMethodsForCurrency } from 'src/models/payment-methods/utils';
import { getUserDisplayName } from 'src/models/user/utils';

import { Button } from './Button';

import type { StripePaymentElementOptions } from '@stripe/stripe-js';
import type { User } from 'src/models/user/_types';

interface StripeFormProps {
  user?: User;
  handlePaymentMethodAdded: (result: any) => void;
}

export const PaymentMethodForm: React.FC<StripeFormProps> = ({
  user,
  handlePaymentMethodAdded,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'paymentMethod' });
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const { session } = useSession();

  useEffect(() => {
    if (session?.stripeToken) {
      setStripePromise(loadStripe(session));
    }
  }, [session]);

  useEffect(() => {
    if (!user || !stripePromise || clientSecret) {
      return;
    }
    // Function to fetch the client secret
    const fetchClientSecret = async () => {
      fetchSetupIntent({
        userId: user.id,
        paymentMethodTypes: getPaymentMethodsForCurrency(
          session?.defaultCurrency
        ),
      }).then((data) => {
        setClientSecret(data.clientSecret);
      });
    };

    fetchClientSecret();
  }, [user, stripePromise, session?.defaultCurrency, clientSecret]);

  if (!clientSecret) {
    return <div>{t('loading')}</div>;
  }

  const options = {
    clientSecret,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm
        user={user}
        handlePaymentMethodAdded={(result) => handlePaymentMethodAdded(result)}
      />
    </Elements>
  );
};

interface CheckoutFormProps {
  user?: User;
  handlePaymentMethodAdded: (result: any) => void;
}
const CheckoutForm: React.FC<CheckoutFormProps> = ({
  user,
  handlePaymentMethodAdded,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'paymentMethod' });
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            name: getUserDisplayName(user),
            email: user?.email,
          },
        },
        expand: ['payment_method'],
      },
      redirect: 'if_required',
    });

    if (result.error) {
      // error
    } else {
      handlePaymentMethodAdded(result);
    }
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    fields: {
      billingDetails: {
        name: 'never',
        email: 'never',
      },
    },
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={1}>
        <PaymentElement options={paymentElementOptions} />
        <Button variant="contained" disabled={!stripe} type="submit">
          {t('submit')}
        </Button>
      </Stack>
    </form>
  );
};

export const getBacsRedirectUrl = async (
  userId: User['id'],
  onWindowClose?: (setupIntent: string) => void
) => {
  const response = await fetchSetupIntent({
    userId,
    paymentMethodTypes: ['bacs_debit'],
  });

  if (response.url) {
    const newWindow = window.open(response.url, '_blank');

    if (newWindow && onWindowClose) {
      // Check periodically if the window has been closed
      const timer = setInterval(() => {
        if (newWindow.closed) {
          clearInterval(timer);
          onWindowClose(response.setupIntent);
        }
      }, 500);
    }
  } else {
    throw new Error(i18n.t('paymentMethod.noRedirectUrl'));
  }
};
